import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, defer, merge, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  take,
  tap,
} from 'rxjs/operators';

import { FormControl, UntypedFormControl } from '@angular/forms';
import { ServiceGrouping } from 'src/app/models/enums/service.enum';
import {
  ServiceGroup,
  ServicesGroupMap,
} from 'src/app/models/service-group.model';
import { Service, ServiceStatus } from 'src/app/models/service.model';
import * as fromStore from 'src/app/store/';
import { objectContainsTerm } from '../../../shared/utils/object-search.util';

const validStatusValues = Object.freeze(Object.keys(ServiceStatus));

const validGroupByValues = Object.freeze(Object.keys(ServiceGrouping));

const defaultGroupByValue = 'Z_Location__r.Name';

const groupByValuesByQueryParam: Record<string, string> = {
  [ServiceGrouping.Location]: defaultGroupByValue,
  [ServiceGrouping.Service]: 'RecordType.Name',
  [ServiceGrouping.Status]: 'Status__c',
};

const groupByQueryParamsByValue = Object.entries(
  groupByValuesByQueryParam,
).reduce(
  (paramsByValue, [param, value]) => ({
    ...paramsByValue,
    [value]: param,
  }),
  {} as Record<string, string>,
);

@Component({
  selector: 'app-inventory-page',
  templateUrl: './inventory-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class InventoryPageComponent implements OnInit {
  // Injected dependencies
  store = inject(Store<fromStore.State>);

  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly router = inject(Router);

  private readonly destroyRef = inject(DestroyRef);

  // Store-selected state
  isLoading$: Observable<boolean> = this.store.select(
    fromStore.getServicesLoading,
  );

  services$: Observable<Service[]> = this.store.select(
    fromStore.getAllServices,
  );

  activeServices$: Observable<Service[]> = this.store.select(
    fromStore.getActiveServices,
  );

  totalActiveLocations$: Observable<number> = this.store.select(
    fromStore.getTotalActiveServiceLocations,
  );

  // Search
  searchTermControl = new FormControl<string>('', { nonNullable: true });

  // Filters - Locations/Services/Status
  locationsFilterOptions$: Observable<string[]> = this.store.select(
    fromStore.getLocationsFilterOptions,
  );

  locationsFilterFormControl: UntypedFormControl = new UntypedFormControl();

  serviceFilterOptions$: Observable<string[]> = this.store.select(
    fromStore.getServiceFilterOptions,
  );

  serviceFilterFormControl: UntypedFormControl = new UntypedFormControl();

  statusFilterOptions$: Observable<string[]> = this.store.select(
    fromStore.getStatusFilterOptions,
  );

  statusFilterFormControl: UntypedFormControl = new UntypedFormControl();

  // Group By Selection
  groupByFormControl: UntypedFormControl = new UntypedFormControl(
    defaultGroupByValue,
  );

  // Is ASC Selection
  isAscFormControl: UntypedFormControl = new UntypedFormControl(true);

  // Selected Service
  selectedServiceAction$ = defer(() =>
    this.selectedServiceSubject.asObservable(),
  );

  // Observable Loading when services are being grouped
  isServicesInProgressSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  isServicesInProgress$ = this.isServicesInProgressSubject.asObservable();

  // Total # of filters selected
  totalSelectedFilters$: Observable<number> = defer(() =>
    combineLatest([
      this.locationFilterValues$,
      this.serviceFilterValues$,
      this.statusFilterValues$,
      this.groupByValue$,
      this.isAscValue$,
    ]).pipe(
      map(([locations, services, status, groupBy, isAsc]) => {
        const totalFiltersSelected =
          Number(locations.length) +
          Number(services.length) +
          Number(status.length) +
          Number(groupBy !== defaultGroupByValue) +
          Number(isAsc !== true);
        return totalFiltersSelected;
      }),
    ),
  );

  // Filters services based on user's selections
  servicesFiltered$: Observable<Service[]> = defer(() =>
    combineLatest([
      this.services$,
      this.locationFilterValues$,
      this.serviceFilterValues$,
      this.statusFilterValues$,
      this.searchTerm$,
    ]).pipe(
      distinctUntilChanged(),
      tap(() => this.isServicesInProgressSubject.next(true)),
      // Short debounce: coalesces a burst of filter changes (e.g. applying
      // several query params on load) into one pass without adding
      // noticeable lag to a single interactive filter change.
      debounceTime(200),
      map(
        ([
          services,
          locationsFilter,
          servicesFilter,
          statusFilter,
          searchTerm,
        ]) => {
          let filteredServices = services;

          filteredServices = this.filterByAllowedValues(
            filteredServices,
            locationsFilter,
            (s) => s.Z_Location__r.Name,
          );

          filteredServices = this.filterByAllowedValues(
            filteredServices,
            servicesFilter,
            (s) => s.RecordType.Name,
          );

          filteredServices = this.filterByAllowedValues(
            filteredServices,
            statusFilter,
            (s) => s.Status__c,
          );

          if (searchTerm && searchTerm.trim().length > 0) {
            const fields = [
              'Name',
              'MRR__c',
              'Bandwidth_Text__c',
              'Connector_Type_Z_Location__c',
              'CustomerLocationAlias',
              'Z_Location__r.Name',
              'RecordType.Name',
              'Status__c',
              'Vendor__r.Name',
              'Vendor__r.Display_Alias__c',
            ];
            filteredServices = filteredServices.filter((service) =>
              objectContainsTerm(service, searchTerm, fields),
            );
          }

          return filteredServices;
        },
      ),
    ),
  );

  // Groups services based on 'group by' selection & sorted
  groupFilteredServices$: Observable<ServiceGroup[]> = defer(() =>
    combineLatest([
      this.servicesFiltered$,
      this.groupByValue$,
      this.isAscValue$,
    ]).pipe(
      distinctUntilChanged(),
      tap(() => this.isServicesInProgressSubject.next(true)),
      map(([services, groupByKey, isAsc]) => {
        const servicesMap = {};

        for (let i = 0; i < services.length; i++) {
          if (groupByKey.includes('.')) {
            const splitKey = groupByKey.split('.');
            if (servicesMap[services[i][splitKey[0]][splitKey[1]]]) {
              servicesMap[services[i][splitKey[0]][splitKey[1]]].totalMrc +=
                services[i].MRR__c;
              servicesMap[
                services[i][splitKey[0]][splitKey[1]]
              ].services.push(services[i]);
            } else {
              servicesMap[services[i][splitKey[0]][splitKey[1]]] = {
                groupTitle: services[i][splitKey[0]][splitKey[1]],
                totalMrc: 0 + services[i].MRR__c,
                services: [services[i]],
              };
            }
          } else {
            const groupTitle = this.toStatusDisplayName(
              services[i][groupByKey],
            );

            if (servicesMap[groupTitle]) {
              servicesMap[groupTitle].totalMrc += services[i].MRR__c;
              servicesMap[groupTitle].services.push(services[i]);
            } else {
              servicesMap[groupTitle] = {
                groupTitle: services[i][groupByKey],
                totalMrc: 0 + services[i].MRR__c,
                services: [services[i]],
              };
            }
          }
        }
        // Sort services object in ASC
        const searchParams = this.activatedRoute.snapshot.queryParamMap;
        const servicesArray = this.sortServiceKeys(
          Object.keys(servicesMap),
          servicesMap,
          searchParams,
        ).map((k) => servicesMap[k]);

        if (!isAsc) {
          servicesArray.reverse();
        }

        return servicesArray;
      }),
      tap(() => this.isServicesInProgressSubject.next(false)),
    ),
  );

  // Current/selected service. Sourced from servicesFiltered$ (not the
  // unfiltered services$) so a selection that no longer matches the active
  // filters resolves to null instead of leaving a stale service in the
  // detail panel. Not deferred: shareReplay(1) must stay a single shared
  // execution across every subscriber (list + detail panel both bind to
  // it), which a defer() wrapper around it would silently defeat.
  selectedService$: Observable<Service | null> = combineLatest([
    this.servicesFiltered$,
    this.selectedServiceAction$,
  ]).pipe(
    map(([services, selectedServiceId]) =>
      selectedServiceId === null
        ? null
        : (services.find((s) => s.Id === selectedServiceId) ?? null),
    ),
    shareReplay(1),
  );

  // Private state
  private isApplyingQueryParams = false;

  private selectedServiceSubject = new BehaviorSubject<string | null>(null);

  // Private derived streams
  private searchTerm$ = defer(() =>
    this.searchTermControl.valueChanges.pipe(
      startWith(this.searchTermControl.value),
      debounceTime(300),
      map((s) => (s ?? '').trim().toLowerCase()),
      distinctUntilChanged(),
    ),
  );

  private locationFilterValues$ = defer(() =>
    this.locationsFilterFormControl.valueChanges.pipe(
      startWith(this.locationsFilterFormControl.value),
      map((locations) => this.toStringArray(locations)),
      distinctUntilChanged((previous, current) =>
        this.areStringArraysEqual(previous, current),
      ),
    ),
  );

  private serviceFilterValues$ = defer(() =>
    this.serviceFilterFormControl.valueChanges.pipe(
      startWith(this.serviceFilterFormControl.value),
      map((services) => this.toStringArray(services)),
      distinctUntilChanged((previous, current) =>
        this.areStringArraysEqual(previous, current),
      ),
    ),
  );

  private statusFilterValues$ = defer(() =>
    this.statusFilterFormControl.valueChanges.pipe(
      startWith(this.statusFilterFormControl.value),
      map((statuses) => this.toStringArray(statuses)),
      distinctUntilChanged((previous, current) =>
        this.areStringArraysEqual(previous, current),
      ),
    ),
  );

  private groupByValue$ = defer(() =>
    this.groupByFormControl.valueChanges.pipe(
      startWith(this.groupByFormControl.value),
      map((groupBy) => this.toGroupByValue(groupBy)),
      distinctUntilChanged(),
    ),
  );

  private isAscValue$ = defer(() =>
    this.isAscFormControl.valueChanges.pipe(
      startWith(this.isAscFormControl.value),
      map((isAsc) => isAsc !== false),
      distinctUntilChanged(),
    ),
  );

  ngOnInit(): void {
    this.applyQueryParams(this.activatedRoute.snapshot.queryParamMap);
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.applyQueryParams(params));
    this.syncControlsToQueryParams();

    this.store
      .select(fromStore.getServicesLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadServices());
        }
      });
  }

  onSetSelectedService(service: string): void {
    this.selectedServiceSubject.next(service);
    this.updateQueryParams({ serviceId: service || null });
  }

  onLoadingIndicator(): void {
    this.isServicesInProgressSubject.next(true);
  }

  private filterByAllowedValues(
    services: Service[],
    filterValues: string[],
    getValue: (service: Service) => string,
  ): Service[] {
    if (filterValues.length === 0) return services;
    const allowedValues = new Set(filterValues);
    return services.filter((service) => allowedValues.has(getValue(service)));
  }

  private sortServiceKeys(
    serviceKeys: string[],
    servicesMap: ServicesGroupMap,
    searchParams: Pick<ParamMap, 'get' | 'getAll'>,
  ): string[] {
    return serviceKeys.sort((a, b) => {
      const groupBy = searchParams.get('groupBy');
      const status = this.toStatusQueryValue(
        searchParams.getAll('status')[0] ?? searchParams.get('status'),
      );
      const groupA = servicesMap[a].groupTitle.replaceAll(' ', '');
      const groupB = servicesMap[b].groupTitle.replaceAll(' ', '');

      if (
        validGroupByValues.includes(groupBy) &&
        validStatusValues.includes(status)
      ) {
        if (groupA === status) {
          return -1;
        }
        if (groupB === status) {
          return 1;
        }
      }
      return groupA.localeCompare(groupB);
    });
  }

  private applyQueryParams(queryParams: ParamMap): void {
    this.isApplyingQueryParams = true;

    try {
      this.setControlValue(
        this.locationsFilterFormControl,
        queryParams.getAll('location'),
      );
      this.setControlValue(
        this.serviceFilterFormControl,
        queryParams.getAll('service'),
      );
      this.setControlValue(
        this.statusFilterFormControl,
        queryParams.getAll('status'),
      );
      this.setControlValue(
        this.groupByFormControl,
        groupByValuesByQueryParam[queryParams.get('groupBy') ?? ''] ??
          defaultGroupByValue,
      );
      this.setControlValue(
        this.isAscFormControl,
        queryParams.get('sort') !== 'desc',
      );
      this.setControlValue(
        this.searchTermControl,
        queryParams.get('q') ?? queryParams.get('services') ?? '',
      );

      const serviceId = queryParams.get('serviceId');
      if (this.selectedServiceSubject.value !== serviceId) {
        this.selectedServiceSubject.next(serviceId);
      }
    } finally {
      this.isApplyingQueryParams = false;
    }
  }

  private syncControlsToQueryParams(): void {
    merge(
      this.locationsFilterFormControl.valueChanges,
      this.serviceFilterFormControl.valueChanges,
      this.statusFilterFormControl.valueChanges,
      this.groupByFormControl.valueChanges,
      this.isAscFormControl.valueChanges,
      this.searchTermControl.valueChanges.pipe(debounceTime(300)),
    )
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isApplyingQueryParams) return;
        this.updateQueryParams(this.buildQueryParams());
      });
  }

  private buildQueryParams(): Params {
    const searchTerm = this.searchTermControl.value.trim();
    const groupBy = this.toGroupByValue(this.groupByFormControl.value);

    return {
      location: this.toQueryParamArray(this.locationsFilterFormControl.value),
      service: this.toQueryParamArray(this.serviceFilterFormControl.value),
      status: this.toQueryParamArray(this.statusFilterFormControl.value),
      groupBy:
        groupBy === defaultGroupByValue
          ? null
          : groupByQueryParamsByValue[groupBy],
      sort: this.isAscFormControl.value === false ? 'desc' : null,
      q: searchTerm || null,
      services: null,
    };
  }

  private updateQueryParams(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      replaceUrl: true,
      queryParams,
    });
  }

  private setControlValue(
    control: UntypedFormControl | FormControl<string>,
    value: string | string[] | boolean,
  ): void {
    if (this.areControlValuesEqual(control.value, value)) return;
    (control as UntypedFormControl).setValue(value);
  }

  private toQueryParamArray(value: unknown): string[] | null {
    const values = this.toStringArray(value);
    return values.length > 0 ? values : null;
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item) => typeof item === 'string' && item.trim().length > 0,
    );
  }

  private toGroupByValue(value: unknown): string {
    return typeof value === 'string' && groupByQueryParamsByValue[value]
      ? value
      : defaultGroupByValue;
  }

  private toStatusQueryValue(value: string | null): string | null {
    return value?.replaceAll(' ', '') ?? null;
  }

  private toStatusDisplayName(status: string): string {
    return status === 'New' ? 'Pending' : status;
  }

  private areControlValuesEqual(
    currentValue: unknown,
    nextValue: string | string[] | boolean,
  ): boolean {
    if (Array.isArray(nextValue)) {
      return this.areStringArraysEqual(
        this.toStringArray(currentValue),
        nextValue,
      );
    }
    return currentValue === nextValue;
  }

  private areStringArraysEqual(previous: string[], current: string[]): boolean {
    return (
      previous.length === current.length &&
      previous.every((value, index) => value === current[index])
    );
  }
}
